import React, { useEffect, useState } from 'react';
import { XIcon, PillIcon, ClockIcon, CalendarIcon, BellIcon, FileTextIcon, ChevronRightIcon, ChevronLeftIcon, SearchIcon, CheckIcon, PackageIcon, AlertCircleIcon } from 'lucide-react';
interface Reminder {
  medicineName: string;
  medicineStrength?: string;
  medicineForm?: string;
  dosage: string;
  frequency: string;
  timesPerDay: number;
  doseTimes: string[];
  daysOfWeek: number[];
  mealTiming: 'before' | 'after' | 'with' | 'none';
  pillsPerDose: number;
  dosesPerIntake: number;
  currentStock?: number;
  treatmentDuration?: number;
  autoReorder: boolean;
  nextDose: Date;
  duration: number;
  reminderType: string[];
  notes?: string;
  status: 'active' | 'completed';
}
interface AddReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reminder: Omit<Reminder, 'id' | 'createdAt'>) => void;
  initialData?: Reminder | null;
}
const SAMPLE_MEDICINES = [{
  name: 'Panadol Extra',
  strength: '500mg',
  form: 'tablets'
}, {
  name: 'Panadol Advance',
  strength: '500mg',
  form: 'tablets'
}, {
  name: 'Brufen',
  strength: '400mg',
  form: 'tablets'
}, {
  name: 'Augmentin',
  strength: '625mg',
  form: 'tablets'
}, {
  name: 'Ventolin',
  strength: '100mcg',
  form: 'inhaler'
}, {
  name: 'Amoxicillin',
  strength: '500mg',
  form: 'capsules'
}, {
  name: 'Vitamin D3',
  strength: '5000 IU',
  form: 'capsules'
}, {
  name: 'Omega-3',
  strength: '1000mg',
  form: 'softgels'
}];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_PRESETS = [{
  label: 'Morning',
  time: '08:00'
}, {
  label: 'Noon',
  time: '12:00'
}, {
  label: 'Evening',
  time: '18:00'
}, {
  label: 'Bedtime',
  time: '22:00'
}];
export function AddReminderModal({
  isOpen,
  onClose,
  onSave,
  initialData
}: AddReminderModalProps) {
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [formData, setFormData] = useState<Omit<Reminder, 'id' | 'createdAt'>>({
    medicineName: '',
    medicineStrength: '',
    medicineForm: '',
    dosage: '1 tablet',
    frequency: 'Once daily',
    timesPerDay: 1,
    doseTimes: ['08:00'],
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    mealTiming: 'none',
    pillsPerDose: 1,
    dosesPerIntake: 1,
    currentStock: undefined,
    treatmentDuration: undefined,
    autoReorder: false,
    nextDose: new Date(),
    duration: 7,
    reminderType: ['push'],
    notes: '',
    status: 'active'
  });
  useEffect(() => {
    if (initialData) {
      setFormData({
        medicineName: initialData.medicineName,
        medicineStrength: initialData.medicineStrength,
        medicineForm: initialData.medicineForm,
        dosage: initialData.dosage,
        frequency: initialData.frequency,
        timesPerDay: initialData.timesPerDay || 1,
        doseTimes: initialData.doseTimes || ['08:00'],
        daysOfWeek: initialData.daysOfWeek || [0, 1, 2, 3, 4, 5, 6],
        mealTiming: initialData.mealTiming || 'none',
        pillsPerDose: initialData.pillsPerDose || 1,
        dosesPerIntake: initialData.dosesPerIntake || 1,
        currentStock: initialData.currentStock,
        treatmentDuration: initialData.treatmentDuration,
        autoReorder: initialData.autoReorder || false,
        nextDose: initialData.nextDose,
        duration: initialData.duration,
        reminderType: initialData.reminderType,
        notes: initialData.notes || '',
        status: initialData.status
      });
      setSearchQuery(initialData.medicineName);
    }
    setStep(1);
  }, [isOpen, initialData]);
  const filteredMedicines = SAMPLE_MEDICINES.filter(med => med.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const selectMedicine = (medicine: (typeof SAMPLE_MEDICINES)[0]) => {
    setFormData({
      ...formData,
      medicineName: medicine.name,
      medicineStrength: medicine.strength,
      medicineForm: medicine.form,
      dosage: `1 ${medicine.form}`
    });
    setSearchQuery(medicine.name);
    setShowSuggestions(false);
  };
  const toggleDay = (day: number) => {
    if (formData.daysOfWeek.includes(day)) {
      setFormData({
        ...formData,
        daysOfWeek: formData.daysOfWeek.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        daysOfWeek: [...formData.daysOfWeek, day].sort()
      });
    }
  };
  const setDayPreset = (preset: 'all' | 'weekdays' | 'weekends') => {
    if (preset === 'all') {
      setFormData({
        ...formData,
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6]
      });
    } else if (preset === 'weekdays') {
      setFormData({
        ...formData,
        daysOfWeek: [0, 1, 2, 3, 4]
      });
    } else {
      setFormData({
        ...formData,
        daysOfWeek: [5, 6]
      });
    }
  };
  const updateTimesPerDay = (times: number) => {
    const newTimes = Array(times).fill('08:00');
    setFormData({
      ...formData,
      timesPerDay: times,
      doseTimes: newTimes
    });
  };
  const updateDoseTime = (index: number, time: string) => {
    const newTimes = [...formData.doseTimes];
    newTimes[index] = time;
    setFormData({
      ...formData,
      doseTimes: newTimes
    });
  };
  const applyTimePreset = (index: number, time: string) => {
    updateDoseTime(index, time);
  };
  const toggleReminderType = (type: string) => {
    if (formData.reminderType.includes(type)) {
      setFormData({
        ...formData,
        reminderType: formData.reminderType.filter(t => t !== type)
      });
    } else {
      setFormData({
        ...formData,
        reminderType: [...formData.reminderType, type]
      });
    }
  };
  const calculateDepletionDate = () => {
    if (!formData.currentStock) return null;
    const dailyConsumption = formData.pillsPerDose * formData.timesPerDay * formData.dosesPerIntake;
    const activeDaysPerWeek = formData.daysOfWeek.length;
    const weeklyConsumption = dailyConsumption * activeDaysPerWeek;
    const daysUntilDepletion = Math.floor(formData.currentStock / dailyConsumption * (7 / activeDaysPerWeek));
    const depletionDate = new Date();
    depletionDate.setDate(depletionDate.getDate() + daysUntilDepletion);
    return {
      date: depletionDate,
      daysLeft: daysUntilDepletion,
      reorderDate: new Date(depletionDate.getTime() - 2 * 24 * 60 * 60 * 1000)
    };
  };
  const depletion = calculateDepletionDate();
  const canProceed = () => {
    if (step === 1) return formData.medicineName.length > 0;
    if (step === 2) return formData.daysOfWeek.length > 0 && formData.timesPerDay > 0;
    if (step === 3) return formData.doseTimes.every(t => t.length > 0);
    if (step === 4) return formData.reminderType.length > 0;
    return true;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  if (!isOpen) return null;
  const totalSteps = 4;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center" data-id="element-3181" data-mp="p7bq58w9iajj">
      <div className="bg-white w-full md:max-w-3xl md:rounded-xl rounded-t-2xl max-h-[90vh] flex flex-col animate-slideUp" data-id="element-3182" data-mp="13vyh67v94qql">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-3183" data-mp="yxn6xw11oe7u">
          <div className="flex items-center gap-3" data-id="element-3184" data-mp="b8c0pz0xdrmb">
            <button onClick={step > 1 ? () => setStep(step - 1) : onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-3185" data-mp="68fz26fpac9x">
              {step > 1 ? <ChevronLeftIcon className="w-5 h-5" data-id="element-3186" data-mp="g6vrccqgrrl0" /> : <XIcon className="w-5 h-5" data-id="element-3187" data-mp="g900pa74tiso" />}
            </button>
            <div data-id="element-3188" data-mp="1vlktmcmzlb42">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-3189" data-mp="1vp8n5bsflxis">
                {initialData ? 'Edit Reminder' : 'Add Medicine Reminder'}
              </h2>
              <p className="text-xs text-gray-500" data-id="element-3190" data-mp="3l5udf7cu7jz0">
                Step {step} of {totalSteps}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:block hidden" data-id="element-3191" data-mp="3v2pbxq941kda">
            <XIcon className="w-5 h-5" data-id="element-3192" data-mp="3otdfgknfc7x" />
          </button>
        </div>
        {/* Progress Bar */}
        <div className="px-4 pt-4" data-id="element-3193" data-mp="7otqoxw0hhde">
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-3194" data-mp="7rze88ozsb70">
            <div className="h-full bg-[#119c4b] transition-all duration-300" style={{
            width: `${step / totalSteps * 100}%`
          }} data-id="element-3195" data-mp="cmv9ritftgov" />
          </div>
        </div>
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-4" data-id="element-3196" data-mp="2jfxq3omji49j">
          {/* Step 1: Select Medicine */}
          {step === 1 && <div className="space-y-4" data-id="element-3197" data-mp="2oeshheykp159">
              <div className="flex items-center gap-3 mb-6" data-id="element-3198" data-mp="15j4f3wms20vn">
                <div className="w-12 h-12 bg-[#1e2b55] bg-opacity-10 rounded-full flex items-center justify-center" data-id="element-3199" data-mp="15kp8vk19pfsg">
                  <SearchIcon className="w-6 h-6 text-[#1e2b55]" data-id="element-3200" data-mp="10qbcpfes74mq" />
                </div>
                <div data-id="element-3201" data-mp="wr2jcbnnl8rp">
                  <h3 className="font-semibold text-gray-900" data-id="element-3202" data-mp="wog9g5daih6b">
                    Select Medicine
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3203" data-mp="1oiieiu3a03tj">
                    Search and choose your medication
                  </p>
                </div>
              </div>
              <div className="relative" data-id="element-3204" data-mp="1kekf0ia1gqzy">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3205" data-mp="1fgivjw8papny">
                  Medicine Name *
                </label>
                <div className="relative" data-id="element-3206" data-mp="1ahzb609r9lxp">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3207" data-mp="42oilo56jycg" />
                  <input type="text" value={searchQuery} onChange={e => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }} onFocus={() => setShowSuggestions(true)} placeholder="Search medicine name..." className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent" data-id="element-3208" data-mp="3v7wxfpntbku7" />
                </div>
                {/* Suggestions */}
                {showSuggestions && searchQuery && filteredMedicines.length > 0 && <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-3209" data-mp="1e0i4iatfa3x7">
                      {filteredMedicines.map((medicine, index) => <button key={index} type="button" onClick={() => selectMedicine(medicine)} className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0" data-id="element-3210" data-mp="3fuhgyzia6rbh" data-mpi={index}>
                          <div className="font-medium text-gray-900" data-id="element-3211" data-mp="1gmhga14dyknc">
                            {medicine.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-3212" data-mp="1lipsp6r77zqt">
                            {medicine.strength} • {medicine.form}
                          </div>
                        </button>)}
                    </div>}
              </div>
              {/* Selected Medicine Badge */}
              {formData.medicineName && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3213" data-mp="12mpydpar0k42">
                  <div className="flex items-center gap-3" data-id="element-3214" data-mp="2s0l74yfofso6">
                    <PillIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-3215" data-mp="2wzxvxoq3z9te" />
                    <div data-id="element-3216" data-mp="2wzftubgrm1dk">
                      <div className="font-semibold text-gray-900" data-id="element-3217" data-mp="2wxlgpjbqjdk1">
                        {formData.medicineName}
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-3218" data-mp="2qinzmhhmgejg">
                        {formData.medicineStrength} • {formData.medicineForm}
                      </div>
                    </div>
                  </div>
                </div>}
              <div data-id="element-3219" data-mp="2gnw4dkbii3ru">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3220" data-mp="3e90gwiyrbnmp">
                  Notes (Optional)
                </label>
                <textarea value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} placeholder="e.g., Allergic to paracetamol..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent resize-none" data-id="element-3221" data-mp="39bi8quskdcfr" />
              </div>
            </div>}
          {/* Step 2: Schedule (Days & Frequency) */}
          {step === 2 && <div className="space-y-6" data-id="element-3222" data-mp="dcg7fqpglyrj">
              <div className="flex items-center gap-3 mb-6" data-id="element-3223" data-mp="dbod0j7lm72u">
                <div className="w-12 h-12 bg-[#119c4b] bg-opacity-10 rounded-full flex items-center justify-center" data-id="element-3224" data-mp="iccddvhpw09q">
                  <CalendarIcon className="w-6 h-6 text-[#119c4b]" data-id="element-3225" data-mp="nadw2fb8dxko" />
                </div>
                <div data-id="element-3226" data-mp="rbpfg33ihmkf">
                  <h3 className="font-semibold text-gray-900" data-id="element-3227" data-mp="rb7i135452ax">Set Schedule</h3>
                  <p className="text-sm text-gray-500" data-id="element-3228" data-mp="w4ilqvemp2ym">
                    Choose days and frequency
                  </p>
                </div>
              </div>
              {/* Day Presets */}
              <div data-id="element-3229" data-mp="162pwuxm8kqsp">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3230" data-mp="1637zqd3em39l">
                  Quick Presets
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-3231" data-mp="27s4y5li6rn32">
                  <button type="button" onClick={() => setDayPreset('all')} className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-[#119c4b] hover:bg-green-50 transition-all text-sm font-medium" data-id="element-3232" data-mp="27qk560bisc7b">
                    Every Day
                  </button>
                  <button type="button" onClick={() => setDayPreset('weekdays')} className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-[#119c4b] hover:bg-green-50 transition-all text-sm font-medium" data-id="element-3233" data-mp="1ot0qoh6am1di">
                    Weekdays
                  </button>
                  <button type="button" onClick={() => setDayPreset('weekends')} className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-[#119c4b] hover:bg-green-50 transition-all text-sm font-medium" data-id="element-3234" data-mp="1tqslbiybo1le">
                    Weekends
                  </button>
                </div>
              </div>
              {/* Days of Week */}
              <div data-id="element-3235" data-mp="3hnrzkl5rusl5">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3236" data-mp="32t3xlgzkycaa">
                  Days of Week *
                </label>
                <div className="grid grid-cols-7 gap-2" data-id="element-3237" data-mp="37sqb7rjblujm">
                  {DAY_NAMES.map((day, index) => <button key={index} type="button" onClick={() => toggleDay(index)} className={`h-12 rounded-lg font-medium text-sm transition-all ${formData.daysOfWeek.includes(index) ? 'bg-[#119c4b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3238" data-mp="3vi1dxek8dz0e" data-mpi={index}>
                      {day}
                    </button>)}
                </div>
              </div>
              {/* Times Per Day */}
              <div data-id="element-3239" data-mp="n3kht57h7f9p">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3240" data-mp="wxgbefuiwe2f">
                  Times Per Day *
                </label>
                <div className="grid grid-cols-4 gap-3" data-id="element-3241" data-mp="rzwrazb5972k">
                  {[1, 2, 3, 4].map(times => <button key={times} type="button" onClick={() => updateTimesPerDay(times)} className={`p-4 border-2 rounded-lg transition-all ${formData.timesPerDay === times ? 'border-[#119c4b] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3242" data-mp="nvgsfhzy6zwu">
                      <div className="text-2xl font-bold text-gray-900" data-id="element-3243" data-mp="4897f1cauw9s">
                        {times}x
                      </div>
                      <div className="text-xs text-gray-600 mt-1" data-id="element-3244" data-mp="9a9i0ozjzru">
                        {times === 1 && 'Once'}
                        {times === 2 && 'Twice'}
                        {times === 3 && '3 times'}
                        {times === 4 && '4 times'}
                      </div>
                    </button>)}
                </div>
              </div>
              {/* Meal Timing */}
              <div data-id="element-3245" data-mp="26xiytglutnla">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3246" data-mp="2bv2cofen3ycp">
                  Meal Timing
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-3247" data-mp="2fvcq2d4rhr1l">
                  {[{
                value: 'before',
                label: 'Before Meal',
                icon: '🍽️'
              }, {
                value: 'after',
                label: 'After Meal',
                icon: '🍴'
              }, {
                value: 'with',
                label: 'With Meal',
                icon: '🥗'
              }, {
                value: 'none',
                label: 'No Preference',
                icon: '⏰'
              }].map(option => <button key={option.value} type="button" onClick={() => setFormData({
                ...formData,
                mealTiming: option.value as any
              })} className={`p-3 border-2 rounded-lg transition-all ${formData.mealTiming === option.value ? 'border-[#119c4b] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3248" data-mp="3p0g3ol3iyu55">
                      <div className="text-2xl mb-1" data-id="element-3249" data-mp="qh97mtxly7pl">{option.icon}</div>
                      <div className="text-sm font-medium" data-id="element-3250" data-mp="lixyymj1u38y">{option.label}</div>
                    </button>)}
                </div>
              </div>
              {/* Dosage */}
              <div className="grid grid-cols-2 gap-4" data-id="element-3251" data-mp="vi6z5x4ice3r">
                <div data-id="element-3252" data-mp="vfkgb1c62c68">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3253" data-mp="1e5xx4dkuqzb2">
                    Pills Per Dose *
                  </label>
                  <input type="number" min="1" value={formData.pillsPerDose} onChange={e => setFormData({
                ...formData,
                pillsPerDose: parseInt(e.target.value) || 1
              })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent" data-id="element-3254" data-mp="1j3r3p7q2hvbv" />
                </div>
                <div data-id="element-3255" data-mp="qg3l1y8cxerv">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3256" data-mp="qheq01djgskk">
                    Doses Per Intake
                  </label>
                  <input type="number" min="1" value={formData.dosesPerIntake} onChange={e => setFormData({
                ...formData,
                dosesPerIntake: parseInt(e.target.value) || 1
              })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent" data-id="element-3257" data-mp="cioq6dzc4esi" />
                </div>
              </div>
            </div>}
          {/* Step 3: Times & Supply */}
          {step === 3 && <div className="space-y-6" data-id="element-3258" data-mp="1492minr7plw9">
              <div className="flex items-center gap-3 mb-6" data-id="element-3259" data-mp="pl95cymhabbk">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-3260" data-mp="pivez32qtnac">
                  <ClockIcon className="w-6 h-6 text-purple-600" data-id="element-3261" data-mp="uhzm7o63xay1" />
                </div>
                <div data-id="element-3262" data-mp="1i8c51u2w4s3e">
                  <h3 className="font-semibold text-gray-900" data-id="element-3263" data-mp="1i7u1ecdwfblg">
                    Set Times & Supply
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3264" data-mp="37q8xjl13fe8m">
                    Configure dose times and stock
                  </p>
                </div>
              </div>
              {/* Dose Times */}
              <div data-id="element-3265" data-mp="3gjm3poo64ccn">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3266" data-mp="3ghsrzuyp71xs">
                  Dose Times *
                </label>
                <div className="space-y-3" data-id="element-3267" data-mp="3cjlldsxy7h0b">
                  {Array.from({
                length: formData.timesPerDay
              }).map((_, index) => <div key={index} data-id="element-3268" data-mp="2ju4vb1xswc79" data-mpi={index}>
                      <div className="flex items-center gap-3 mb-2" data-id="element-3269" data-mp="2jv6758mn52q1">
                        <span className="text-sm font-medium text-gray-700 w-16" data-id="element-3270" data-mp="2xu5vef68rflo">
                          Dose {index + 1}
                        </span>
                        <input type="time" value={formData.doseTimes[index] || '08:00'} onChange={e => updateDoseTime(index, e.target.value)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent" data-id="element-3271" data-mp="d1uc4bdy1uez" />
                      </div>
                      <div className="flex gap-2 ml-20" data-id="element-3272" data-mp="3fvjrxw60d3sr">
                        {TIME_PRESETS.map(preset => <button key={preset.label} type="button" onClick={() => applyTimePreset(index, preset.time)} className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors" data-id="element-3273" data-mp="2j5snp57x029r">
                            {preset.label}
                          </button>)}
                      </div>
                    </div>)}
                </div>
              </div>
              {/* Medicine Stock */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3274" data-mp="1wvau5ie9zaey">
                <div className="flex items-center gap-2 mb-3" data-id="element-3275" data-mp="1rwr2d1d034eo">
                  <PackageIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-3276" data-mp="1rvg2hcmoegsu" />
                  <h4 className="font-semibold text-gray-900" data-id="element-3277" data-mp="1mwl8dej9d5tf">
                    Medicine Stock (Optional)
                  </h4>
                </div>
                <div className="space-y-3" data-id="element-3278" data-mp="1dx802p6venaa">
                  <div data-id="element-3279" data-mp="1dz2l03i56fed">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3280" data-mp="1dzkmbejnvjt5">
                      Current Stock (pills)
                    </label>
                    <input type="number" min="0" value={formData.currentStock || ''} onChange={e => setFormData({
                  ...formData,
                  currentStock: parseInt(e.target.value) || undefined
                })} placeholder="How many pills do you have?" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent" data-id="element-3281" data-mp="148q1mem2cs9t" />
                  </div>
                  {/* Auto-Reorder Toggle */}
                  {formData.currentStock && <div data-id="element-3282" data-mp="26mfhnu4lxptb">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-3283" data-mp="26lw7x0p427qd">
                        <input type="checkbox" checked={formData.autoReorder} onChange={e => setFormData({
                    ...formData,
                    autoReorder: e.target.checked
                  })} className="w-5 h-5 text-[#119c4b] rounded focus:ring-2 focus:ring-[#119c4b]" data-id="element-3284" data-mp="2bls2yaftyots" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-3285" data-mp="awmdwitp9n2e">
                          Auto-Reorder 2 days before running out
                        </span>
                      </label>
                      {/* Depletion Info */}
                      {depletion && <div className="mt-3 p-3 bg-white border border-blue-300 rounded-lg" data-id="element-3286" data-mp="eyzl7bo3ht1a">
                          <div className="flex items-start gap-2" data-id="element-3287" data-mp="tndm3h1yb7f7">
                            <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-3288" data-mp="tpqeyp98z271" />
                            <div className="text-sm" data-id="element-3289" data-mp="oqn6euu26dub">
                              <p className="font-medium text-gray-900 mb-1" data-id="element-3290" data-mp="12naglzb0v0i6">
                                Will run out on{' '}
                                {depletion.date.toLocaleDateString()}
                              </p>
                              <p className="text-gray-600" data-id="element-3291" data-mp="34fymyhiyq426">
                                {depletion.daysLeft} days left •{' '}
                                {formData.autoReorder && <span className="text-[#119c4b] font-medium" data-id="element-3292" data-mp="13f0jvtbs61kh">
                                    Reorder reminder on{' '}
                                    {depletion.reorderDate.toLocaleDateString()}
                                  </span>}
                              </p>
                            </div>
                          </div>
                        </div>}
                    </div>}
                </div>
              </div>
            </div>}
          {/* Step 4: Notifications & Summary */}
          {step === 4 && <div className="space-y-6" data-id="element-3293" data-mp="2117ubjurmo4r">
              <div className="flex items-center gap-3 mb-6" data-id="element-3294" data-mp="2106bgt7icxch">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center" data-id="element-3295" data-mp="260khy6tgnz8e">
                  <BellIcon className="w-6 h-6 text-yellow-600" data-id="element-3296" data-mp="2a1mhypv7cfbf" />
                </div>
                <div data-id="element-3297" data-mp="3ujyhcn0x1nbu">
                  <h3 className="font-semibold text-gray-900" data-id="element-3298" data-mp="3ukgbne40prh4">
                    Notifications & Summary
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3299" data-mp="3qgs0klg6gdr4">
                    Choose channels and review
                  </p>
                </div>
              </div>
              {/* Notification Channels */}
              <div data-id="element-3300" data-mp="3gm03diqibswv">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3301" data-mp="3gmi7t2nbpdfv">
                  Notification Channels * (Select at least one)
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-3302" data-mp="d3yqgv66nrzu">
                  {[{
                type: 'push',
                label: 'Push',
                icon: '🔔'
              }, {
                type: 'sms',
                label: 'SMS',
                icon: '💬'
              }, {
                type: 'whatsapp',
                label: 'WhatsApp',
                icon: '📱'
              }, {
                type: 'email',
                label: 'Email',
                icon: '📧'
              }].map(({
                type,
                label,
                icon
              }) => <button key={type} type="button" onClick={() => toggleReminderType(type)} className={`p-4 border-2 rounded-lg transition-all ${formData.reminderType.includes(type) ? 'border-[#119c4b] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3303" data-mp="3vkripqop3mvr">
                      <div className="text-2xl mb-2" data-id="element-3304" data-mp="e6ng4h4ct755">{icon}</div>
                      <div className="text-sm font-medium" data-id="element-3305" data-mp="j2b5awiu60an">{label}</div>
                      {formData.reminderType.includes(type) && <CheckIcon className="w-4 h-4 text-[#119c4b] mx-auto mt-2" data-id="element-3306" data-mp="2yx4rc76haup6" />}
                    </button>)}
                </div>
              </div>
              {/* Summary */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-3307" data-mp="2rbsh0yva7igm">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-3308" data-mp="35b0i5xb090ng">📋 Summary</h4>
                <div className="space-y-2 text-sm" data-id="element-3309" data-mp="358xu3itexhld">
                  <div className="flex justify-between" data-id="element-3310" data-mp="30bnujacb7pjt">
                    <span className="text-gray-600" data-id="element-3311" data-mp="30a4ijvxos0nu">Medicine:</span>
                    <span className="font-medium text-gray-900" data-id="element-3312" data-mp="2cmw9d2x507gj">
                      {formData.medicineName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3313" data-mp="2mco5w2osrann">
                    <span className="text-gray-600" data-id="element-3314" data-mp="2me94c3e8fdqs">Dosage:</span>
                    <span className="font-medium text-gray-900" data-id="element-3315" data-mp="2hko404p627of">
                      {formData.pillsPerDose} pill(s) ×{' '}
                      {formData.dosesPerIntake} dose(s)
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3316" data-mp="1ouo6iqwzvwvl">
                    <span className="text-gray-600" data-id="element-3317" data-mp="mxt6bvx7cgkr">Frequency:</span>
                    <span className="font-medium text-gray-900" data-id="element-3318" data-mp="i6b6jb6x0m1d">
                      {formData.timesPerDay}x per day
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3319" data-mp="d4vntps7mrm5">
                    <span className="text-gray-600" data-id="element-3320" data-mp="88wppah2xv8j">Days:</span>
                    <span className="font-medium text-gray-900" data-id="element-3321" data-mp="15vdm8dhzo4t8">
                      {formData.daysOfWeek.map(d => DAY_NAMES[d]).join(', ')}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3322" data-mp="vxfydr41ka15">
                    <span className="text-gray-600" data-id="element-3323" data-mp="vvv5e5xdkz5e">Times:</span>
                    <span className="font-medium text-gray-900" data-id="element-3324" data-mp="rwmauyuigx8s">
                      {formData.doseTimes.join(', ')}
                    </span>
                  </div>
                  {formData.mealTiming !== 'none' && <div className="flex justify-between" data-id="element-3325" data-mp="3cla9yu3978df">
                      <span className="text-gray-600" data-id="element-3326" data-mp="1s4aul1b7eq2u">Meal:</span>
                      <span className="font-medium text-gray-900 capitalize" data-id="element-3327" data-mp="1s1y8qd2brvki">
                        {formData.mealTiming} meal
                      </span>
                    </div>}
                  <div className="flex justify-between" data-id="element-3328" data-mp="212mopdba4601">
                    <span className="text-gray-600" data-id="element-3329" data-mp="25whb4oi9qz2t">Notifications:</span>
                    <span className="font-medium text-gray-900" data-id="element-3330" data-mp="2av22dyfjm8f4">
                      {formData.reminderType.join(', ').toUpperCase()}
                    </span>
                  </div>
                  {formData.currentStock && <>
                      <div className="flex justify-between" data-id="element-3331" data-mp="2jt4aexxp21bw">
                        <span className="text-gray-600" data-id="element-3332" data-mp="2ot8usz1q3g7e">Current Stock:</span>
                        <span className="font-medium text-gray-900" data-id="element-3333" data-mp="phpjwewuzxv3">
                          {formData.currentStock} pills
                        </span>
                      </div>
                      {formData.autoReorder && depletion && <div className="flex justify-between" data-id="element-3334" data-mp="1xrgc6gh648vr">
                          <span className="text-gray-600" data-id="element-3335" data-mp="1ssvcqj9qpt8d">Auto-Reorder:</span>
                          <span className="font-medium text-[#119c4b]" data-id="element-3336" data-mp="26rmnbvpe8wrr">
                            {depletion.reorderDate.toLocaleDateString()}
                          </span>
                        </div>}
                    </>}
                </div>
              </div>
            </div>}
        </div>
        {/* Footer Buttons */}
        <div className="p-4 border-t border-gray-200 flex gap-3" data-id="element-3337" data-mp="2bp4tjec10xw2">
          {step < totalSteps ? <>
              <button type="button" onClick={onClose} className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-3338" data-mp="2lh0serlxss1c">
                Cancel
              </button>
              <button type="button" onClick={() => setStep(step + 1)} disabled={!canProceed()} className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${canProceed() ? 'bg-[#1e2b55] text-white hover:bg-[#162041]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-3339" data-mp="3e391lqoneom5">
                Next
                <ChevronRightIcon className="w-4 h-4" data-id="element-3340" data-mp="2vc7swaxq7epe" />
              </button>
            </> : <>
              <button type="button" onClick={() => setStep(step - 1)} className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-3341" data-mp="304j1012me6t1">
                Back
              </button>
              <button type="submit" onClick={handleSubmit} disabled={!canProceed()} className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${canProceed() ? 'bg-[#119c4b] text-white hover:bg-[#0f8a42]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-3342" data-mp="2cey92sohnz7m">
                {initialData ? 'Update Reminder' : 'Save Reminder'}
              </button>
            </>}
        </div>
      </div>
    </div>;
}